import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Target,
  Eye,
  Handshake,
  ClipboardCheck,
  Layers,
  ShieldCheck,
} from "lucide-react";
import Reveal from "@/components/site/Reveal";
import AboutCycle from "@/components/site/AboutCycle";
import PageSeo from "@/components/site/PageSeo";
import { ABOUT } from "@/constants/testIds";
import { useWebsiteContent } from "@/hooks/useWebsiteContent";

const ICONS = { Target, Eye, Handshake, ClipboardCheck, Layers, ShieldCheck };

export default function About() {
  const { data: wc } = useWebsiteContent();
  const sections = wc?.aboutSections || [];
  const values   = wc?.aboutValues || [];
  return (
    <main data-testid={ABOUT.root}>
      <PageSeo
        path="/about"
        title="About MTS — Who we are"
        description="We run projects in Asia so you don't have to. Learn how MTS acts as an accountable execution partner across sourcing, manufacturing, quality control and international trading."
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home",
                item: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
              { "@type": "ListItem", position: 2, name: "About" },
            ],
          },
          // AboutPage schema — signals to search engines and AI assistants
          // that this URL describes the organization itself. mainEntity
          // references the site-wide Organization node emitted by
          // SiteSchemas.jsx via its @id (no duplication of Organization data).
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "About Manufacturing and Trading Services (MTS)",
            url: (typeof window !== "undefined" ? window.location.origin : "") + "/about",
            description: "We run projects in Asia so you don't have to. Learn how MTS acts as an accountable execution partner across sourcing, manufacturing, quality control and international trading.",
            isPartOf: { "@id": (typeof window !== "undefined" ? window.location.origin : "") + "/#website" },
            mainEntity: { "@id": (typeof window !== "undefined" ? window.location.origin : "") + "/#organization" },
          },
        ]}
      />
      {/* HEADER ========================================================== */}
      <section className="border-b border-[#ececef] mts-soft-gradient relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 mts-dot-grid opacity-30 [mask-image:radial-gradient(60%_60%_at_50%_50%,black,transparent)]"
        />
        <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]">
                  {wc?.aboutHeroEyebrow || ""}
                </p>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="mt-6 text-[44px] sm:text-[60px] md:text-[80px] font-semibold tracking-[-0.03em] leading-[1.02] text-[#0a0a0a] max-w-4xl text-balance">
                  {wc?.aboutHeroTitlePrefix || ""}{" "}
                  <span
                    className="italic font-normal"
                    style={{ fontFamily: "'Instrument Serif', serif" }}
                  >
                    {wc?.aboutHeroTitleItalic || ""}
                  </span>
                </h1>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-8 text-[17px] md:text-[19px] leading-relaxed text-[#424245] max-w-2xl text-pretty">
                  {wc?.aboutHeroSubtitle || ""}
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={200}>
                <div className="relative mx-auto max-w-[420px] mts-float">
                  <div className="absolute -inset-6 rounded-[28px] bg-white/60 backdrop-blur border border-[#ececef] shadow-[0_20px_60px_-30px_rgba(0,0,0,0.18)]" />
                  <AboutCycle className="relative w-full h-auto" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* THREE SECTIONS ================================================== */}
      {sections.map((s, idx) => (
        <section
          key={s.tag || idx}
          className={`border-b border-[#ececef] ${
            idx % 2 === 0 ? "bg-white" : "bg-[#f7f7f8]"
          }`}
        >
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
              <div className="md:col-span-4">
                <Reveal>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]">
                    {s.tag}
                  </p>
                </Reveal>
                <Reveal delay={80}>
                  <h2 className="mt-6 text-[32px] md:text-[44px] font-semibold tracking-[-0.03em] text-[#0a0a0a] leading-[1.05]">
                    {s.heading}
                  </h2>
                </Reveal>
              </div>
              <div className="md:col-span-8 space-y-6">
                {(s.paragraphs || []).map((p, i) => (
                  <Reveal key={i} delay={120 + i * 60}>
                    <p className="text-[16px] leading-[1.7] text-[#424245] max-w-2xl">
                      {p.text}
                    </p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* VALUES ========================================================== */}
      <section className="border-b border-[#ececef] bg-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]">
              {wc?.aboutValuesEyebrow || ""}
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-6 text-[36px] sm:text-[44px] md:text-[56px] font-semibold tracking-[-0.03em] text-[#0a0a0a] max-w-3xl leading-[1.05] text-balance">
              {wc?.aboutValuesTitlePrefix || ""}{" "}
              <span
                className="italic font-normal"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                {wc?.aboutValuesTitleItalic || ""}
              </span>
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((v, i) => {
              const IconCmp = ICONS[v.icon] || Target;
              return (
                <Reveal key={v.title || i} delay={100 + i * 40}>
                  <div className="mts-card p-7 md:p-8 h-full">
                    <div className="flex items-center gap-4">
                      <div className="mts-icon-chip shrink-0">
                        <IconCmp size={20} strokeWidth={1.5} />
                      </div>
                      <h3 className="text-[18px] md:text-[20px] font-medium text-[#0a0a0a] tracking-tight leading-snug">
                        {v.title}
                      </h3>
                    </div>
                    <p className="mt-4 text-[14px] leading-relaxed text-[#6e6e73] max-w-md">
                      {v.description}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA — dark ====================================================== */}
      <section className="relative overflow-hidden">
        <div className="mts-dark-panel">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
              <div className="md:col-span-8">
                <Reveal>
                  <h2 className="text-[36px] sm:text-[48px] md:text-[64px] font-semibold tracking-[-0.03em] text-white leading-[1.05] max-w-2xl text-balance">
                    {wc?.aboutCtaTitlePrefix || ""}{" "}
                    <span
                      className="italic font-normal text-white/90"
                      style={{ fontFamily: "'Instrument Serif', serif" }}
                    >
                      {wc?.aboutCtaTitleItalic || ""}
                    </span>
                  </h2>
                </Reveal>
              </div>
              <div className="md:col-span-4 md:text-right">
                <Reveal delay={100}>
                  <Link
                    to="/contact"
                    className="mts-btn-inverse"
                  >
                    {wc?.aboutCtaLabel || "Contact Us"} <ArrowRight size={16} />
                  </Link>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
